<template>
  <div class="position-relative">
    <div
      @click="goTo"
      :class="{clickable: !isRemoved, 'research-removed': isRemoved}"
      :id="`card-research-${index || _uid}`"
    >
      <div
        class="card card-research mb-3"
        :style="{'background-image': backgroundImage}"
        @mouseenter="!isRemoved ? showOverlay = true : null"
        @mouseleave="!isRemoved ? showOverlay = false : null"
      >
        <div
          class="card-research-inner d-flex flex-column justify-content-between"
        >
          <transition name="fade">
            <div
              class="d-flex justify-content-between commercial-card-top p-1"
              v-if="isReviewed && !showOverlay"
            >
              <div class="d-flex justify-content-between w-100">
                <v-badge
                  icon="check"
                  color-type="success"
                />
                <v-badge
                  v-if="research.commercial.average_rating"
                  :title="$t('word.average_rating')"
                  color-type="success"
                  :text="research.commercial.average_rating.toFixed(1)"
                />
              </div>
            </div>
          </transition>
          <transition name="fade">
            <div
              class="d-flex justify-content-between align-items-end card-research-bottom p-2"
              :class="{'card-research-bottom-down': showOverlay}"
            >
              <div class="w-50">
                <v-badge
                  v-if="research.has_coins && research.coins_per_review > 0"
                  :title="$t('phrase.earn_coins', { count: research.coins_per_review })"
                  icon="coins"
                  :text="research.coins_per_review.toString()"
                />
                <v-badge
                  v-if="publishedAt"
                  :title="$t('word.publication_date')"
                  icon="calendar"
                  :text="stringToPrettyMonthDate(publishedAt)"
                />
              </div>
              <div class="m-1 text-right w-50">
                <img
                  v-if="research.organisation.logo"
                  class="organisation-logo"
                  :src="research.organisation.logo"
                  :alt="research.organisation.name"
                />
                <div
                  v-else
                  class="organisation-logo text-white text-truncate"
                >
                  {{ research.organisation.name }}
                </div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div
              v-if="showOverlay"
              class="research-overlay d-flex justify-content-center align-items-center"
            >
              <div class="logo-img-wrapper w-75 d-flex justify-content-center align-items-center">
                <img
                  class="w-75"
                  src="@/common/assets/img/logo.png"
                  alt="commercialreview"
                />
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
    <div
      v-if="isRemoved"
      class="position-absolute w-100 h-100"
      style="top: 0; left: 0;"
    >
      <div class="d-flex justify-content-center align-items-center h-100">
        <div class="badge badge-danger">
          {{ $t('phrase.removed_from_research_short') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Badge from '@/common/components/Badge.vue';
import { stringToPrettyMonthDate } from '@/common/utils/formatting';
import { mapGetters } from 'vuex';

export default {
  name: 'ReviewerResearchCard',
  components: {
    'v-badge': Badge,
  },
  props: {
    research: {
      type: Object,
      required: true,
    },
    index: {
      required: false,
    },
  },
  data() {
    return {
      showOverlay: false,
    };
  },
  methods: {
    stringToPrettyMonthDate,
    goTo(e) {
      if (e.target.className.includes('organisation-logo')) {
        this.$router.push({
          name: 'organisation-detail',
          params: { slug: this.research.organisation.slug },
        });
      } else {
        if (this.isRemoved) {
          return;
        }
        this.$router.push(this.researchRoute);
      }
    },
  },
  computed: {
    ...mapGetters(['user']),
    publishedAt() {
      if (this.research.commercial && this.research.commercial.published_at) {
        return this.research.commercial.published_at;
      }
      return null;
    },
    membershipSlugs() {
      if (!this.user.memberships) {
        return [];
      }
      return this.user.memberships.map((membership) => membership.organisation.slug);
    },
    isReviewed() {
      if (this.research.session) {
        return !!this.research.session.reviewed;
      }
      return false;
    },
    isRemoved() {
      if (this.research.session) {
        return !!this.research.session.removed;
      }
      return false;
    },
    researchRoute() {
      if (!this.user.isAuthenticated) {
        return {
          name: 'review-research',
          params: { uuid: this.research.uuid },
        };
      }

      if (this.membershipSlugs.includes(this.research.organisation.slug)) {
        return { name: 'own-commercial' };
      }

      if (this.isReviewed) {
        return {
          name: 'research-answers',
          params: { uuid: this.research.uuid },
        };
      }

      if (this.user.config.max_review_reached) {
        return { name: 'max-reviews' };
      }
      return {
        name: 'review-research',
        params: { uuid: this.research.uuid },
      };
    },
    backgroundImage() {
      if (!this.research.commercial) {
        return '';
      }
      if (this.research.commercial.image_file) {
        return `url('${this.research.commercial.image_file}')`;
      }
      if (this.research.commercial.youtube_image_url) {
        return `url('${this.research.commercial.youtube_image_url}')`;
      }
      if (this.research.commercial.commercial_type === 'radio') {
        // eslint-disable-next-line global-require
        return `url('${require('@/common/assets/img/radio.jpg')}')`;
      }
      return '';
    },
  },
};
</script>


<style scoped>
.card-research {
  width: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  padding: 28%;
  box-sizing: border-box;
  position: relative;
}

.card-research-inner {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.commercial-reviewed {
  background: linear-gradient(rgba(170, 246, 218, 0.4), rgba(255, 255, 255, 0));
}

.card-research-bottom {
  z-index: 1000;
  position: absolute;
  bottom: 0;
  width: 100%;
  transition: all 0.5s ease;
  height: 50px;
  background: linear-gradient(rgba(255, 255, 255, 0), rgba(0, 0, 0, 1));
}

.research-overlay {
  background: rgba(0, 0, 0, 0.2);
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
}

.research-overlay .logo-img-wrapper {
  background: rgba(255, 255, 255, 0.7);
  padding: 30px;
  border-radius: 100%;
}

.organisation-logo {
  max-width: 100%;
  max-height: 30px;
  width: auto;
}

.research-removed {
  opacity: 0.5;
}

.research-removed .badge {
  opacity: 1 !important;
}
</style>
